import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiGet } from "@/lib/api";
import type { NavNode, PageDto } from "@/types";
import { RichText } from "@/components/content/RichText";
import { Breadcrumb } from "@/components/layout/Breadcrumb";

export function StaticPage() {
  const location = useLocation();
  const path = location.pathname.replace(/^\/+/, "");
  const { data: page, isLoading, error } = useQuery({
    queryKey: ["page", path, "de"],
    queryFn: () => apiGet<PageDto>("/public/pages/" + path + "?locale=" + "de"),
    retry: false,
  });
  const { data: nav = [] } = useQuery({
    queryKey: ["nav", "de"],
    queryFn: () => apiGet<NavNode[]>("/public/navigation?locale=" + "de"),
    staleTime: 5 * 60_000,
  });

  if (isLoading) return <div className="animate-pulse text-text-muted">...</div>;
  if (error || !page) {
    return (
      <div className="text-center py-20">
        <h1 className="text-5xl font-bold mb-2">404</h1>
        <p className="text-text-muted">{"Diese Seite wurde leider nicht gefunden."}</p>
        <Link to="/" className="text-primary hover:underline mt-4 inline-block">{"Zur Startseite"}</Link>
      </div>
    );
  }

  const segments = path.split("/").filter(Boolean);
  const topSlug = segments[0];
  const section = nav.find((n) => n.slug === topSlug);

  return (
    <article>
      <Helmet>
        <title>{page.title} | Falkenrehde</title>
      </Helmet>
      <Breadcrumb />
      <h1 className="text-3xl lg:text-4xl font-bold mb-6">{page.title}</h1>

      {section && section.children.length > 0 && (
        <nav aria-label="Bereichsnavigation" className="mb-8 flex flex-wrap gap-2 border-b border-border pb-3">
          <Link to={section.path} className={pillClass(section.path === "/" + path)}>{section.title}</Link>
          {section.children.map((c) => (
            <Link key={c.slug} to={c.path} className={pillClass(c.path === "/" + path)}>{c.title}</Link>
          ))}
        </nav>
      )}

      <RichText html={page.bodyHtml} />
    </article>
  );
}

function pillClass(active: boolean) {
  return active
    ? "inline-block px-3 py-1.5 rounded-full text-sm bg-primary text-white"
    : "inline-block px-3 py-1.5 rounded-full text-sm bg-bg-muted text-text hover:bg-primary-soft hover:text-primary";
}
